import React, { useEffect, useState } from 'react';
import { AopLink, Card, InternalCard, Modal, Pill, WarnRow } from '../ui';
import { builderInternals, figmaTemplateUrl, templateCategoriesByChannel, TEMPLATE_CHANNELS } from '../copData';
import TemplatePreview from './TemplatePreview';
import CampaignRules from './CampaignRules';
import CampaignDesign from './CampaignDesign';
import { useCop } from '../CopApp';

const BUILDER_TABS = [
	{ code: 'build', label: 'Build' },
	{ code: 'rules', label: 'Rules' },
	{ code: 'design', label: 'Design' },
];

const DEFAULT_CSS = {
	campaignRadius: '16px',
	campaignPadding: '30px',
	textColor: '#ffffff',
	imageOverlayOpacity: '0.2',
	headlineSize: '1.75em',
	buttonBackground: '#111827',
	eyelineSize: '0.75em',
	buttonTextSize: '0.9em',
	buttonRadius: '8px',
	buttonPadding: '0.5em 1.5em',
	closeIconSize: '1em',
	imagePanelOpacity: '1',
	gradientStart: '#ec4899',
	gradientEnd: '#3b82f6',
};

const DEFAULT_RULES = {
	device: 'all',
	testing: false,
	visitor: 'any',
	customer: 'any',
	loggedState: 'any',
	pages: '',
	suppressedPages: '',
	requiredElements: '',
	requiredCookies: '',
	suppressCookies: '',
	countries: '',
	states: '',
	trafficSource: '',
	minSubtotal: '',
	maxSubtotal: '',
	pageVisitsMin: '',
	pageVisitsMax: '',
	isEntry: false,
	isAfter: '',
	isBefore: '',
	trafficPercent: 100,
};

// Operator-edited Figma links persist (per template node) across navigation and
// sessions via localStorage — the seam to a server-side override later.
const LINKS_KEY = 'cop:figmaLinks';
function loadLinkOverrides() {
	try {
		return JSON.parse(localStorage.getItem(LINKS_KEY)) || {};
	} catch {
		return {};
	}
}
function saveLinkOverrides(map) {
	try {
		localStorage.setItem(LINKS_KEY, JSON.stringify(map));
	} catch {
		/* ignore storage failures (private mode, quota) */
	}
}

function useCountdown() {
	// Demo offer deadline ~8.4h out, ticking live like the mockup.
	const [deadline] = useState(() => Date.now() + (8 * 3600 + 24 * 60 + 55) * 1000);
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const t = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(t);
	}, []);
	const left = Math.max(0, Math.floor((deadline - now) / 1000));
	const pad = (n) => String(n).padStart(2, '0');
	return { h: pad(Math.floor(left / 3600)), m: pad(Math.floor((left % 3600) / 60)), s: pad(left % 60) };
}

const READINESS = [
	{ label: 'Event coverage validated', ok: true },
	{ label: 'Audience definition stable, suppressions applied', ok: true },
	{ label: 'Creative + offer tokens resolved', ok: true },
	{ label: 'Measurement plan: lift test, 30d or significance', ok: true },
	{ label: 'No conflicting live campaign on this segment', ok: true },
];

export default function CampaignBuilder() {
	const { builderSegment, submitForApproval, showToast, navigate, isInternal, segments } = useCop();
	const initial = segments.find((s) => s.segment_id === builderSegment) || segments[1];
	const [selected, setSelected] = useState(initial);
	const [device, setDevice] = useState('desktop');
	const [config, setConfig] = useState({ exitIntent: true, browsingDelay: true, scoreThreshold: false });
	const [readinessOpen, setReadinessOpen] = useState(false);
	const [tab, setTab] = useState('build');
	const [creative, setCreative] = useState({
		eyeline: 'Get 40% off your order',
		headline: "Wait! Don't leave yet...",
		subtext: 'Complete your order now and we’ll take care of the rest.',
		question: 'What brought you in today?',
		options: 'Just browsing, Looking for a gift, Ready to buy',
		inputPlaceholder: 'you@email.com',
		buttonText: 'Claim Offer',
		imageUrl: '',
		coupon: initial.offer_token || '',
	});
	const [css, setCss] = useState(DEFAULT_CSS);
	const [rules, setRules] = useState(DEFAULT_RULES);
	const { h, m, s } = useCountdown();

	// Creative template picker — choose a channel (onsite / email), a category,
	// and a template; the preview reflects the choice and the link deep-links to
	// the matching frame in Figma. Each channel keeps its own selection so the
	// links to both selected templates stay visible. Defaults to the onsite
	// category matching the segment's recommended solution family.
	const onsiteCategories = templateCategoriesByChannel('onsite');
	const emailCategories = templateCategoriesByChannel('email');
	const initialCategory =
		onsiteCategories.find((c) => c.solution_family === initial.recommended_solution_family) || onsiteCategories[0];
	const [channel, setChannel] = useState('onsite');
	const [selections, setSelections] = useState({
		onsite: { code: initialCategory.code, nodeId: initialCategory.templates[0].node_id },
		email: { code: emailCategories[0].code, nodeId: emailCategories[0].templates[0].node_id },
	});
	const [linkOverrides, setLinkOverrides] = useState(loadLinkOverrides);

	const channelCategories = templateCategoriesByChannel(channel);
	const sel = selections[channel];
	const category = channelCategories.find((c) => c.code === sel.code) || channelCategories[0];
	const template = category.templates.find((t) => t.node_id === sel.nodeId) || category.templates[0];
	const categoryCode = category.code;
	const templateId = template.node_id;
	const figmaLink = linkOverrides[templateId] ?? figmaTemplateUrl(templateId);
	const linkEdited = Object.prototype.hasOwnProperty.call(linkOverrides, templateId);

	const setSelection = (code, nodeId) => setSelections((prev) => ({ ...prev, [channel]: { code, nodeId } }));
	const onChannelChange = (ch) => setChannel(ch);
	const onCategoryChange = (code) => {
		const next = channelCategories.find((c) => c.code === code) || channelCategories[0];
		setSelection(code, next.templates[0].node_id);
	};
	const onTemplateChange = (nodeId) => setSelection(category.code, nodeId);

	// Save the edited link (per template) immediately so it survives remounts.
	const onLinkChange = (value) => {
		setLinkOverrides((prev) => {
			const next = { ...prev, [templateId]: value };
			saveLinkOverrides(next);
			return next;
		});
	};
	const resetLink = () => {
		setLinkOverrides((prev) => {
			const next = { ...prev };
			delete next[templateId];
			saveLinkOverrides(next);
			return next;
		});
	};
	const openFigma = () => {
		if (figmaLink) window.open(figmaLink, '_blank', 'noopener,noreferrer');
	};

	// Links to the selected template in each channel, honoring saved overrides.
	const selectedTemplateLinks = TEMPLATE_CHANNELS.map((ch) => {
		const cats = templateCategoriesByChannel(ch.code);
		const s = selections[ch.code];
		const cat = cats.find((c) => c.code === s.code) || cats[0];
		const tmpl = cat.templates.find((t) => t.node_id === s.nodeId) || cat.templates[0];
		return {
			channelCode: ch.code,
			channelName: ch.name,
			label: `${cat.name} · ${tmpl.name}`,
			url: linkOverrides[tmpl.node_id] ?? figmaTemplateUrl(tmpl.node_id),
		};
	});

	// Bundle of template state + handlers shared with the Design tab.
	const tpl = {
		channel,
		channels: TEMPLATE_CHANNELS,
		channelCategories,
		category,
		categoryCode,
		template,
		templateId,
		figmaLink,
		linkEdited,
		linkOverrides,
		figmaUrl: figmaTemplateUrl,
		selectedTemplateLinks,
		onChannelChange,
		onCategoryChange,
		onTemplateChange,
		onLinkChange,
		resetLink,
		openFigma,
	};

	useEffect(() => {
		const fromUrl = segments.find((seg) => seg.segment_id === builderSegment);
		if (fromUrl) setSelected(fromUrl);
	}, [builderSegment, segments]);

	const activate = () => {
		setReadinessOpen(false);
		submitForApproval({
			approval_request_id: `apr-${Math.floor(Math.random() * 9000) + 4000}`,
			action_type: 'launch_campaign',
			action_label: 'Launch Campaign',
			risk_level: selected.discount_pct >= 15 ? 'high' : 'medium',
			agent: 'campaign_draft_bot',
			model: 'Claude Opus',
			justification: `Launch ${selected.segment_name} campaign — ${selected.recommended_action}`,
			impact_chip: `+${selected.projected_engagement}% engagement projected`,
			approvers: selected.discount_pct >= 15 ? 'Pricing Lead + VP Commerce' : 'Growth Lead',
			approver_count: selected.discount_pct >= 15 ? 2 : 1,
			rollback: true,
			expanded: false,
			evidence: [
				`Segment: ${selected.audience_size.toLocaleString()} visitors, ${selected.conversion_rate}% baseline conversion`,
				`Offer: ${selected.offer_token}${selected.discount_pct ? ` (${selected.discount_pct}%)` : ' (no incentive)'}`,
				`Estimated reach ${(selected.projected_reach_mo / 1000).toFixed(1)}K/mo`,
			],
			impact: {
				projected_uplift: `+${selected.projected_engagement}% engagement`,
				incremental_revenue: 'lift-test measured',
				margin_impact: selected.discount_pct ? `-${selected.discount_pct}% on redeemed` : '$0',
			},
			linked: [`Segment: ${selected.segment_name}`],
			sla: selected.discount_pct >= 15 ? '2 hr window' : '8 hr window',
			entity: selected.segment_name,
		});
		navigate('approvals');
	};

	return (
		<main className="cop-page">
			<div className="cop-page-head">
				<div className="cop-page-head-main">
					<h1 className="cop-page-title">
						Campaign Builder <Pill tone="purple">AI-assisted</Pill>
					</h1>
					<div className="cop-page-sub">Drafts route through readiness checks and the approval queue — nothing launches directly.</div>
				</div>
				<div className="cop-page-head-actions">
					<button className="cop-btn" onClick={() => showToast('Draft saved')}>Save as Draft</button>
					<button className="cop-btn primary" onClick={() => setReadinessOpen(true)}>Activate Campaign</button>
				</div>
			</div>

			<div className="cop-builder-tabs">
				{BUILDER_TABS.map((t) => (
					<button
						key={t.code}
						className={`cop-builder-tab${tab === t.code ? ' active' : ''}`}
						onClick={() => setTab(t.code)}
					>
						{t.label}
					</button>
				))}
			</div>

			{tab === 'rules' && <CampaignRules rules={rules} setRules={setRules} selected={selected} />}
			{tab === 'design' && <CampaignDesign tpl={tpl} creative={creative} setCreative={setCreative} css={css} setCss={setCss} />}

			{tab === 'build' && (
			<div className="cop-grid cop-builder-grid">
				<div>
					<div className="cop-nav-label" style={{ paddingLeft: 2 }}>Target audience</div>
					{segments.map((seg) => (
						<button
							key={seg.segment_id}
							className={`cop-audience-item${selected.segment_id === seg.segment_id ? ' active' : ''}`}
							onClick={() => setSelected(seg)}
						>
							<div className="n">
								<span className="cop-legend-dot" style={{ color: seg.color, background: seg.color }} />
								{seg.segment_name}
							</div>
							<div className="d">
								{seg.audience_size.toLocaleString()} visitors · {seg.conversion_rate}% conv
							</div>
						</button>
					))}
				</div>

				<Card
					title="Campaign Preview"
					icon="▣"
					right={
						<span className="cop-filter-row">
							{['desktop', 'mobile'].map((d) => (
								<button key={d} className={`cop-filter-chip${device === d ? ' active' : ''}`} onClick={() => setDevice(d)}>
									{d}
								</button>
							))}
						</span>
					}
				>
					<div className="cop-preview-stage">
						<TemplatePreview
							channel={channel}
							categoryCode={categoryCode}
							template={template}
							selected={selected}
							device={device}
							countdown={{ h, m, s }}
						/>
					</div>
					<div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--text-dim)', display: 'flex', gap: 8, alignItems: 'center' }}>
						<Pill tone="pink">AI recommendation</Pill>
						{selected.recommended_action}
					</div>
				</Card>

				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Card title="Campaign Configuration" icon="⚙">
						<div className="cop-config-row">
							<span className="k">Offer type</span>
							<b>{selected.discount_pct ? `Personalized ${selected.discount_pct}% discount` : 'No-incentive tactic'}</b>
						</div>
						<div className="cop-config-row">
							<span className="k">Exit intent</span>
							<button
								className={`cop-toggle${config.exitIntent ? ' on' : ''}`}
								onClick={() => setConfig((c) => ({ ...c, exitIntent: !c.exitIntent }))}
								aria-label="toggle exit intent"
							/>
						</div>
						<div className="cop-config-row">
							<span className="k">Browsing delay (45s)</span>
							<button
								className={`cop-toggle${config.browsingDelay ? ' on' : ''}`}
								onClick={() => setConfig((c) => ({ ...c, browsingDelay: !c.browsingDelay }))}
								aria-label="toggle browsing delay"
							/>
						</div>
						<div className="cop-config-row">
							<span className="k">Intent score threshold (0.7)</span>
							<button
								className={`cop-toggle${config.scoreThreshold ? ' on' : ''}`}
								onClick={() => setConfig((c) => ({ ...c, scoreThreshold: !c.scoreThreshold }))}
								aria-label="toggle score threshold"
							/>
						</div>
						<div className="cop-config-row">
							<span className="k">Personalization token</span>
							<span className="cop-mono" style={{ color: '#f9a8d4' }}>{`{{first_name}}, {{cart_items}}`}</span>
						</div>
						<div className="cop-config-row">
							<span className="k">Creative template</span>
							<button className="cop-link" onClick={() => setTab('design')}>
								{TEMPLATE_CHANNELS.find((c) => c.code === channel)?.name} · {template.name} →
							</button>
						</div>
						<div className="cop-config-row">
							<span className="k">Targeting rules</span>
							<button className="cop-link" onClick={() => setTab('rules')}>Configure company_json →</button>
						</div>
					</Card>

					<Card title="Projected Impact" icon="↗">
						<div className="cop-projection">
							<span>Engagement rate</span>
							<b className="cop-num tone-green">+{selected.projected_engagement}%</b>
						</div>
						<div className="cop-projection">
							<span>AOV delta</span>
							<b className={`cop-num ${selected.projected_aov_delta >= 0 ? 'tone-green' : 'tone-red'}`}>
								{selected.projected_aov_delta >= 0 ? '+' : ''}
								{selected.projected_aov_delta}%
							</b>
						</div>
						<div className="cop-projection">
							<span>Measurement plan</span>
							<b>Lift test · 30d / significance</b>
						</div>
						<div className="cop-reach">
							<div className="v">{(selected.projected_reach_mo / 1000).toFixed(1)}K <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>/ mo</span></div>
							<div className="l">Estimated reach</div>
						</div>
						<div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
							Projections are estimates, not measured lift. Incrementality is proven post-launch by the lift test.
						</div>
					</Card>

					{isInternal && (
						<InternalCard title="Build Internals" icon="🛠">
							{builderInternals.map(([k, v]) => (
								<div className="cop-config-row" key={k}>
									<span className="k">{k}</span>
									<span className="cop-mono" style={{ fontSize: 11, color: v.includes('FAILING') ? 'var(--red)' : 'var(--text-sec)' }}>{v}</span>
								</div>
							))}
							<div style={{ marginTop: 6 }}>
								<WarnRow level="error">VIP10 coupon failing validation — fix in Admin or swap the offer token before this draft can pass readiness</WarnRow>
							</div>
							<div className="cop-aop-links">
								<AopLink view="manual-trigger">copy_bot / design_bot</AopLink>
								<AopLink view="reports">AOP packets</AopLink>
							</div>
						</InternalCard>
					)}
				</div>
			</div>
			)}

			{readinessOpen && (
				<Modal
					title="Launch readiness"
					sub={`${selected.segment_name} · ${selected.recommended_solution_family.replace(/_/g, ' ')} · all gates must pass before approval`}
					onClose={() => setReadinessOpen(false)}
					actions={
						<>
							<button className="cop-btn" onClick={() => setReadinessOpen(false)}>Cancel</button>
							<button className="cop-btn primary" onClick={activate}>Submit for Approval</button>
						</>
					}
				>
					{READINESS.map((r) => (
						<div className="cop-check-row" key={r.label}>
							<span style={{ color: r.ok ? 'var(--green)' : 'var(--red)' }}>{r.ok ? '✓' : '✕'}</span>
							{r.label}
						</div>
					))}
				</Modal>
			)}
		</main>
	);
}
